import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import industrySolutionsData from './industrySolutionsData';
import './IndustrialAutomationSection.css';

const SOLUTION_SLUGS = [
  'water-monitoring',
  'energy-metering',
  'air-quality',
  'steam-measurement',
  'optical-crm'
];

/** Up to four feature cells for the 2×2 grid, same sources as industry solution pages (modules, impact, highlights). */
function getFeatureCells(solution) {
  const cells = [];
  (solution.modules || []).forEach((m) => {
    cells.push({ title: m.name, text: m.text });
  });
  (solution.impactCards || []).forEach((c) => {
    cells.push({ title: c.title, text: c.text });
  });
  (solution.highlights || []).forEach((h) => {
    cells.push({ title: 'What you get', text: h });
  });
  return cells.slice(0, 4);
}

function solutionHref(slug) {
  return slug === 'optical-crm' ? '/optical-crm' : `/industry-solutions/${slug}`;
}

const IndustrialAutomationSection = () => {
  const solutions = SOLUTION_SLUGS.map((slug) => {
    const data = industrySolutionsData[slug];
    if (!data) return null;
    return { slug, ...data, features: getFeatureCells(data) };
  }).filter(Boolean);

  const [visible, setVisible] = useState([]);
  const stripRefs = useRef([]);

  useEffect(() => {
    const observers = stripRefs.current.map((ref, index) => {
      if (!ref) return null;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisible((prev) => [...new Set([...prev, index])]);
            }
          });
        },
        { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
      );
      observer.observe(ref);
      return observer;
    });
    return () => observers.forEach((o) => o?.disconnect());
  }, []);

  return (
    <div className="industrial-auto" id="industrial-automation">
      <header className="industrial-auto__intro">
        <div className="industrial-auto__intro-inner">
          <h2>
            Industrial <span className="industrial-auto__intro-accent">Automation</span>
          </h2>
          <p>
            We design intelligent automation systems that optimize production lines, reduce downtime, and improve operational visibility across your industrial workflows.
          </p>
        </div>
      </header>

      {solutions.map((item, index) => {
        const imageOnRight = index % 2 === 1;
        return (
          <section
            key={item.slug}
            ref={(el) => {
              stripRefs.current[index] = el;
            }}
            className={`industrial-auto__strip ${imageOnRight ? 'industrial-auto__strip--flip' : ''} ${
              visible.includes(index) ? 'industrial-auto__strip--visible' : ''
            }`}
            aria-labelledby={`industrial-auto-title-${item.slug}`}
          >
            <div className="industrial-auto__strip-decor" aria-hidden="true">
              <span className="industrial-auto__squiggle industrial-auto__squiggle--1" />
              <span className="industrial-auto__squiggle industrial-auto__squiggle--2" />
              <span className="industrial-auto__blob industrial-auto__blob--a" />
              <span className="industrial-auto__blob industrial-auto__blob--b" />
            </div>

            <div className="industrial-auto__strip-inner">
              <div className="industrial-auto__media">
                <div className="industrial-auto__blob-frame" aria-hidden="true" />
                <img
                  src={item.heroImage}
                  alt=""
                  className="industrial-auto__hero-img"
                />
              </div>

              <div className="industrial-auto__copy">
                <p className="industrial-auto__kicker">Fracto Industry Solutions</p>
                <h3 className="industrial-auto__title" id={`industrial-auto-title-${item.slug}`}>
                  {item.title}
                </h3>
                {item.shortDescription && (
                  <p className="industrial-auto__tagline">{item.shortDescription}</p>
                )}

                <div className="industrial-auto__feature-grid">
                  {item.features.map((cell, fi) => (
                    <div className="industrial-auto__feature" key={`${item.slug}-f-${fi}`}>
                      <div className="industrial-auto__check" aria-hidden="true">
                        <svg width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 5.5L5 9.5L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <div className="industrial-auto__feature-body">
                        <h4>{cell.title}</h4>
                        <p>{cell.text}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Link className="industrial-auto__cta" to={solutionHref(item.slug)}>
                  View full solution
                  <span aria-hidden="true"> →</span>
                </Link>
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default IndustrialAutomationSection;
